import React from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@/contexts/AuthContext';
import {
  Home, CreditCard, FileText, BookOpen, Key,
  AlertTriangle, User, LogOut
} from 'lucide-react';

const navItems = [
  { to: '/my-studio', label: 'Home', icon: Home },
  { to: '/my-payments', label: 'Payments', icon: CreditCard },
  { to: '/my-contract', label: 'Contract', icon: FileText },
  { to: '/my-access', label: 'Access', icon: Key },
  { to: '/my-profile', label: 'Profile', icon: User },
];

export default function PractitionerLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] pb-20 md:pb-0">
      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-b border-[#E5E2DC] z-50 px-4 py-3 flex items-center justify-between">
        <span className="font-heading text-base font-medium text-[#3A4035]"><img src="/logo.webp" alt="Go Freestyle Studios" className="h-10" style={{filter:'contrast(1.15) brightness(0.95)'}} /></span>
        <button onClick={handleLogout} className="p-2 hover:bg-[#F2F0EB] rounded-xl transition-colors" data-testid="mobile-logout-button">
          <LogOut className="w-4 h-4 text-[#5C5C55]" strokeWidth={1.5} />
        </button>
      </header>

      {/* Desktop header */}
      <header className="hidden md:flex fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-b border-[#E5E2DC] z-50 px-8 py-4 items-center justify-between">
        <span className="font-heading text-lg font-medium text-[#3A4035]" data-testid="brand-header"><img src="/logo.webp" alt="Go Freestyle Studios" className="h-12" style={{filter:'contrast(1.15) brightness(0.95)'}} /></span>
        <nav className="flex items-center gap-1">
          {navItems.map(item => (
            <NavLink key={item.to} to={item.to}
              className={({ isActive }) => `flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${isActive ? 'bg-[#3A4035] text-white' : 'text-[#5C5C55] hover:bg-[#F2F0EB]'}`}
              data-testid={`nav-${item.label.toLowerCase()}`}>
              <item.icon className="w-4 h-4" strokeWidth={1.5} />
              <span className="hidden lg:inline">{item.label}</span>
            </NavLink>
          ))}
          <NavLink to="/house-rules"
            className={({ isActive }) => `flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${isActive ? 'bg-[#3A4035] text-white' : 'text-[#5C5C55] hover:bg-[#F2F0EB]'}`}
            data-testid="nav-rules">
            <BookOpen className="w-4 h-4" strokeWidth={1.5} />
            <span className="hidden lg:inline">Rules</span>
          </NavLink>
          <NavLink to="/report-issue"
            className={({ isActive }) => `flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${isActive ? 'bg-[#3A4035] text-white' : 'text-[#5C5C55] hover:bg-[#F2F0EB]'}`}
            data-testid="nav-support">
            <AlertTriangle className="w-4 h-4" strokeWidth={1.5} />
            <span className="hidden lg:inline">Support</span>
          </NavLink>
          <div className="w-px h-6 bg-[#E5E2DC] mx-2" />
          <div className="flex items-center gap-3">
            <span className="text-sm text-[#5C5C55]">{user?.name}</span>
            <button onClick={handleLogout} className="p-2 hover:bg-[#F2F0EB] rounded-full transition-colors" data-testid="desktop-logout-button">
              <LogOut className="w-4 h-4 text-[#5C5C55]" strokeWidth={1.5} />
            </button>
          </div>
        </nav>
      </header>

      {/* Main content */}
      <main className="pt-14 md:pt-20 min-h-screen">
        <div className="px-4 sm:px-8 py-6 max-w-3xl mx-auto">
          <Helmet><meta name="robots" content="noindex, nofollow" /></Helmet>
          <Outlet />
        </div>
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-[#E5E2DC] z-50 shadow-[0_-4px_20px_rgba(0,0,0,0.03)]">
        <div className="flex items-center justify-around px-2 py-2">
          {navItems.map(item => (
            <NavLink key={item.to} to={item.to}
              className={({ isActive }) => `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl text-[10px] font-medium transition-colors ${isActive ? 'text-[#3A4035]' : 'text-[#8A8B85]'}`}
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}>
              <item.icon className="w-5 h-5" strokeWidth={1.5} />
              {item.label}
            </NavLink>
          ))}
          <NavLink to="/report-issue"
            className={({ isActive }) => `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl text-[10px] font-medium transition-colors ${isActive ? 'text-[#3A4035]' : 'text-[#8A8B85]'}`}
            data-testid="mobile-nav-support">
            <AlertTriangle className="w-5 h-5" strokeWidth={1.5} />
            Support
          </NavLink>
        </div>
      </nav>
    </div>
  );
}
